import type { ComponentType, ReactNode } from 'react';
import { useEffect, useState } from 'react';

type CmpEmbedProviderProps = {
  baseUrl: string;
  children: ReactNode;
  onTokenExpired?: () => void;
  orgSsoId: string;
  token: string;
};

type EmbeddableTaskProps = {
  className?: string;
  defaultTab?: 'content' | 'history';
  onApprove?: () => void;
  onReject?: (reason: string) => void;
  taskId: string;
};

type CmpModules = {
  CmpEmbedProvider: ComponentType<CmpEmbedProviderProps>;
  EmbeddableTask: ComponentType<EmbeddableTaskProps>;
};

let cached: CmpModules | null = null;
let inflight: Promise<CmpModules> | null = null;
let polyfilled = false;

async function loadModules(): Promise<CmpModules> {
  if (cached) return cached;
  if (inflight) return inflight;

  inflight = (async () => {
    const [{ default: CmpEmbedProvider }, { default: EmbeddableTask }, moment, _] =
      await Promise.all([
        import('MFECmp/CmpEmbedProvider'),
        import('MFECmp/EmbeddableTask'),
        import('moment'),
        import('lodash-es'),
      ]);

    if (!polyfilled) {
      window.moment = moment.default ?? moment;
      // @ts-expect-error -- stub lodash for CMP
      window._ = _;
      // @ts-expect-error -- stub window.analytics for CMP
      window.analytics ??= {
        group: () => {},
        identify: () => {},
        load: () => {},
        page: () => {},
        track: () => {},
      };
      polyfilled = true;
    }

    cached = { CmpEmbedProvider, EmbeddableTask };
    inflight = null;
    return cached;
  })();

  return inflight;
}

export function useCmpModules(load: boolean) {
  const [modules, setModules] = useState<CmpModules | null>(cached);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!load) return;
    if (cached) {
      setModules(cached);
      return;
    }
    let cancelled = false;
    loadModules()
      .then(m => {
        if (!cancelled) setModules(m);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  return { ...modules, isLoaded: modules !== null, error };
}
